/* ============================================================
   Dr Forest — client-side fuzzy search (df-fuzzy-search.js)
   Searches the whole catalogue (products, collections, pages,
   blog articles) from a cached JSON feed. Hyphen/space
   insensitive, typo tolerant, plural aware, synonym routed.
   Powers: the header search dropdown + the /search results page.

   Synonyms and popular links live in df-search-synonyms.js.
   Edit that file, not this one.

   v6.4 — 21 Aug 2026
     * Proper plural handling (strawberries -> Strawberry).
     * Synonym routing for other brands, crops, symptoms, sizes.
       When the whole query is a routed phrase ("miracle gro",
       "bone meal"), the routed products lead; otherwise direct
       matches lead and routed ones fill in behind.
     * Pack sizes and SKUs are searchable ("9kg", "DF-BLOOM-9").
     * Out-of-stock products rank below in-stock ones.
     * Searches now report to Shopify analytics, so the
       Search & Discovery app can show zero-result queries.
     * Popular links before typing, and instead of a dead end.
   v6.3 — NPK codes, fuzzy threshold, product order, results page.
   ============================================================ */
(function () {
  'use strict';

  var FEED_URL = '/collections/all?view=searchfeed';
  var CACHE_KEY = 'df_feed_v3';
  var CACHE_TTL = 30 * 60 * 1000;            /* 30 min */
  var COVERAGE = 0.5;                        /* fraction of query words that must match */
  var MIN_CHARS = 2;
  var MAX = { product: 6, collection: 4, page: 3, article: 3 };
  var SYN_MAX = 3;                           /* products a single route may add */
  var DEBOUNCE = 110;
  var REPORT_IDLE = 1500;                    /* ms of stillness before reporting a search */

  var index = null;
  var loadingPromise = null;
  var reported = {};

  /* ---------- text utils ---------- */
  function squish(s) { return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ''); }
  function norm(s) { return (s || '').toLowerCase().replace(/[^a-z0-9\s]+/g, ' ').replace(/\s+/g, ' ').trim(); }
  function esc(s) { return (s || '').replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }
  function isDigits(t) { return /^[0-9]+$/.test(t); }

  /* Light stemmer. Enough for shop language — not linguistics.
     strawberries -> strawberry, tomatoes -> tomato, roses -> rose,
     leaves -> leaf. Leaves grass, citrus, gypsum alone. */
  function stem(w) {
    if (w.length > 4 && /ies$/.test(w)) return w.slice(0, -3) + 'y';
    if (w.length > 4 && /ves$/.test(w)) return w.slice(0, -3) + 'f';
    if (w.length > 4 && /oes$/.test(w)) return w.slice(0, -2);
    if (w.length > 4 && /(ches|shes|sses|xes|zes)$/.test(w)) return w.slice(0, -2);
    if (w.length > 3 && /s$/.test(w) && !/(ss|us|is)$/.test(w)) return w.slice(0, -1);
    return w;
  }

  function lev(a, b) {
    var m = a.length, n = b.length;
    if (Math.abs(m - n) > 2) return 3;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= n; j++) prev[j] = j;
    for (i = 1; i <= m; i++) {
      cur[0] = i;
      for (j = 1; j <= n; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      }
      for (j = 0; j <= n; j++) prev[j] = cur[j];
    }
    return prev[n];
  }

  /* ---------- index ---------- */
  function build(data) {
    var items = [];
    function add(type, title, url, extra, image, price, available) {
      if (!title || !url) return;
      var text = title + ' ' + (extra || '');
      var words = norm(text).split(' ');
      var stems = words.map(stem);
      items.push({
        type: type, title: title, url: url, image: image || '', price: price || '',
        available: available !== 0,
        hay: norm(text), titleNorm: norm(title), sq: squish(text),
        words: words, stems: stems, stemHay: ' ' + stems.join(' ') + ' '
      });
    }
    (data.products || []).forEach(function (p) {
      add('product', p.t, p.u, (p.g || '') + ' ' + (p.y || '') + ' ' + (p.b || '') + ' ' + (p.s || ''), p.i, p.p, p.a);
    });
    (data.collections || []).forEach(function (c) { add('collection', c.t, c.u); });
    (data.pages || []).forEach(function (p) { add('page', p.t, p.u); });
    (data.articles || []).forEach(function (a) { add('article', a.t, a.u); });
    return items;
  }

  function load() {
    if (index) return Promise.resolve(index);
    if (loadingPromise) return loadingPromise;
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var obj = JSON.parse(raw);
        if (obj && obj.t && (Date.now() - obj.t) < CACHE_TTL && obj.d && obj.d.products) { index = build(obj.d); if (index.length) return Promise.resolve(index); }
      }
    } catch (e) {}
    loadingPromise = fetch(FEED_URL)
      .then(function (r) { return r.text(); })
      .then(function (txt) {
        var data = JSON.parse(txt.trim());
        if (!data.products || !data.products.length || data.products[0].t === undefined) throw new Error('bad feed');
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: data })); } catch (e) {}
        index = build(data);
        return index;
      })
      .catch(function () { index = null; loadingPromise = null; return []; });
    return loadingPromise;
  }

  /* ---------- matching ---------- */
  function tokenScore(tok, tokStem, it) {
    if (it.hay.indexOf(tok) !== -1) return 1;
    if (it.stemHay.indexOf(' ' + tokStem + ' ') !== -1) return 0.95;
    /* One edit for short words, two for long ones. At distance 2 a
       five-letter word like "bloom" collides with "blossom"; at nine
       letters "malassess" still reaches "molasses". */
    var best = 0, w, i, maxd = tok.length <= 6 ? 1 : 2;
    for (i = 0; i < it.words.length; i++) {
      w = it.words[i];
      if (!w) continue;
      if (w.indexOf(tok) === 0) { best = Math.max(best, 0.9); continue; }
      if (Math.abs(w.length - tok.length) <= maxd && lev(w, tok) <= maxd) best = Math.max(best, tok.length <= 4 ? 0.72 : 0.8);
    }
    return best;
  }

  function titleMatch(it, tok, tokStem) {
    if (it.titleNorm.indexOf(tok) !== -1) return true;
    if (tokStem.length > 2 && it.titleNorm.indexOf(tokStem) !== -1) return true;
    return it.titleNorm.indexOf(tokStem + 's') !== -1;
  }

  /* One pass of plain matching. search() wraps this with synonyms.
     strict = every query word must match; used for routed searches
     so "sulphur soil acidifier" returns the acidifier, not every
     product that happens to mention soil. */
  function rank(q, strict) {
    q = (q || '').trim();
    if (q.length < MIN_CHARS || !index) return [];

    var qnorm = norm(q);
    var qsq = squish(q);

    var allTokens = qnorm.split(' ').filter(Boolean);
    var digitGroups = allTokens.filter(isDigits);

    /* An NPK code — "2-8-4", "2.8.4", "2 8 4", "npk 2-8-4". Two or
       more digit groups are one code, matched against the
       punctuation-free index ("284"), never as loose single digits. */
    var npkLike = digitGroups.length >= 2;
    var npkSq = digitGroups.join('');

    var qtokens = allTokens.filter(function (t) {
      if (npkLike && isDigits(t)) return false;
      return t.length >= MIN_CHARS;
    });
    var qstems = qtokens.map(stem);

    if (!npkLike && !qtokens.length) return [];

    var need = strict ? 1 : COVERAGE;
    var out = [], i, j;
    for (i = 0; i < index.length; i++) {
      var it = index[i], sum = 0, matched = 0, titleHit = false;

      if (npkLike && it.sq.indexOf(npkSq) === -1) continue;

      for (j = 0; j < qtokens.length; j++) {
        var m = tokenScore(qtokens[j], qstems[j], it);
        if (m > 0) { sum += m; matched++; if (titleMatch(it, qtokens[j], qstems[j])) titleHit = true; }
      }

      var coverage = qtokens.length ? matched / qtokens.length : 1;
      var compound = !strict && qsq.length >= 3 && it.sq.indexOf(qsq) !== -1;

      if (strict) { if (coverage < 1) continue; }
      else if (!(npkLike || coverage >= need || compound)) continue;

      var phrase = qnorm.length >= 3 && it.titleNorm.indexOf(qnorm) !== -1;

      var score = sum +
        (npkLike ? 2.5 : 0) +
        (compound ? 2.5 : 0) +
        (phrase ? 3.0 : 0) +
        (titleHit ? 1.2 : 0) +
        (it.type === 'product' ? 1.4 : 0) +
        (it.available ? 0 : -0.8) +
        coverage;
      out.push({ it: it, s: score });
    }
    out.sort(function (a, b) { return b.s - a.s; });
    return out.map(function (r) { return r.it; });
  }

  /* ---------- synonyms ---------- */
  function synonymTargets(qnorm) {
    var map = window.DF_SEARCH_SYNONYMS || {};
    var padded = ' ' + qnorm + ' ';
    var targets = [], exact = false, key;
    for (key in map) {
      if (!Object.prototype.hasOwnProperty.call(map, key)) continue;
      if (padded.indexOf(' ' + key + ' ') === -1) continue;
      if (targets.indexOf(map[key]) === -1) targets.push(map[key]);
      if (qnorm === key) exact = true;
    }
    return { targets: targets, exact: exact };
  }

  function search(q) {
    var direct = rank(q, false);
    var syn = synonymTargets(norm(q));
    if (!syn.targets.length) return direct;

    var routed = [], picked = {};
    syn.targets.forEach(function (term) {
      var hits = rank(term, true);
      if (!hits.length) hits = rank(term, false);
      var added = 0;
      hits.forEach(function (it) {
        if (added >= SYN_MAX || picked[it.url] || it.type !== 'product') return;
        picked[it.url] = 1; routed.push(it); added++;
      });
    });

    /* Whole query is a routed phrase — the route is the better answer
       than whatever the letters happened to match. */
    var ordered = syn.exact ? routed.concat(direct) : direct.concat(routed);
    var seen = {}, out = [];
    ordered.forEach(function (it) { if (!seen[it.url]) { seen[it.url] = 1; out.push(it); } });
    return out;
  }

  function group(results) {
    var g = { product: [], collection: [], page: [], article: [] };
    results.forEach(function (it) { if (g[it.type] && g[it.type].length < MAX[it.type]) g[it.type].push(it); });
    return g;
  }

  /* ---------- analytics ----------
     Publishes the standard Shopify search event so search terms —
     including the ones that find nothing — appear in Shopify
     Analytics and the Search & Discovery app. Without this the
     custom dropdown is invisible to reporting. */
  function report(q, results) {
    q = (q || '').trim();
    if (!q || reported[q.toLowerCase()]) return;
    reported[q.toLowerCase()] = 1;
    var products = (results || []).filter(function (it) { return it.type === 'product'; });
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('search_submitted', {
          searchResult: {
            query: q,
            productVariants: products.map(function (it) { return { product: { title: it.title, url: it.url } }; })
          }
        });
      }
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('df:search', { detail: { query: q, results: products.length } }));
    } catch (e) {}
  }

  /* ---------- dropdown ---------- */
  var HEADINGS = { collection: 'Collections', page: 'Pages', article: 'Guides', product: 'Products' };

  function popularBlock(heading) {
    var list = window.DF_SEARCH_POPULAR || [];
    if (!list.length) return '';
    var h = '<div class="predictive-search__result-group"><div><h2 class="predictive-search__heading text-body caption-with-letter-spacing">' + esc(heading) + '</h2>' +
      '<ul class="predictive-search__results-list list-unstyled" role="group">';
    list.forEach(function (p) {
      h += '<li class="predictive-search__list-item" role="option" aria-selected="false" data-df-url="' + esc(p.url) + '">' +
        '<a href="' + esc(p.url) + '" class="predictive-search__item link link--text" tabindex="-1">' +
        '<div class="predictive-search__item-content predictive-search__item-content--centered">' +
        '<p class="predictive-search__item-heading h5">' + esc(p.label) + '</p></div></a></li>';
    });
    return h + '</ul></div></div>';
  }

  function suggestList(items) {
    var h = '<ul class="predictive-search__results-list list-unstyled" role="group">';
    items.forEach(function (it) {
      h += '<li class="predictive-search__list-item" role="option" aria-selected="false" data-df-url="' + esc(it.url) + '">' +
        '<a href="' + esc(it.url) + '" class="predictive-search__item link link--text" tabindex="-1">' +
        '<div class="predictive-search__item-content predictive-search__item-content--centered">' +
        '<p class="predictive-search__item-heading h5">' + esc(it.title) + '</p></div></a></li>';
    });
    return h + '</ul>';
  }

  function productList(items) {
    var h = '<ul class="predictive-search__results-list list-unstyled" role="group">';
    items.forEach(function (it) {
      var img = it.image ? '<img class="predictive-search__image" src="' + esc(it.image) + '" alt="" width="50" height="50" loading="lazy">' : '';
      h += '<li class="predictive-search__list-item" role="option" aria-selected="false" data-df-url="' + esc(it.url) + '">' +
        '<a href="' + esc(it.url) + '" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">' +
        img + '<div class="predictive-search__item-content">' +
        '<p class="predictive-search__item-heading h5">' + esc(it.title) + '</p>' +
        (it.price ? '<span class="price">' + esc(it.price) + '</span>' : '') +
        '</div></a></li>';
    });
    return h + '</ul>';
  }

  function renderEmptyState(box) {
    var h = popularBlock('Popular');
    if (!h) { box.hidden = true; box.innerHTML = ''; return; }
    box.innerHTML = '<div class="predictive-search__results-groups-wrapper">' + h + '</div>';
    box.hidden = false;
  }

  function renderDropdown(box, q, results) {
    if (!q || q.length < MIN_CHARS) { renderEmptyState(box); return; }
    var g = group(results), h = '';
    var hasAny = g.product.length || g.collection.length || g.page.length || g.article.length;
    if (!hasAny) {
      h = '<div class="predictive-search__results-groups-wrapper">' +
        '<div class="predictive-search__result-group"><p class="predictive-search__no-results" style="padding:1.4rem 2rem;font-family:var(--df-sans,sans-serif);color:var(--df-inkSoft,#5a7a68);">Nothing matches &ldquo;' + esc(q) + '&rdquo;. Try a plant name, a nutrient, or an NPK code.</p></div>' +
        popularBlock('Popular') + '</div>';
    } else {
      h = '<div class="predictive-search__results-groups-wrapper">';
      /* Products first — someone searching a product name should not
         have to read past a collection and two guides to find it. */
      if (g.product.length) h += '<div class="predictive-search__result-group"><div><h2 class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>' + productList(g.product) + '</div></div>';
      var col2 = '';
      ['collection', 'page', 'article'].forEach(function (t) {
        if (g[t].length) col2 += '<div><h2 class="predictive-search__heading text-body caption-with-letter-spacing">' + HEADINGS[t] + '</h2>' + suggestList(g[t]) + '</div>';
      });
      if (col2) h += '<div class="predictive-search__result-group">' + col2 + '</div>';
      h += '</div>';
    }
    h += '<div class="predictive-search__search-for-button"><a href="/search?q=' + encodeURIComponent(q) + '" class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" data-df-url="/search?q=' + encodeURIComponent(q) + '">' +
      '<span>Search for &ldquo;' + esc(q) + '&rdquo;</span><span class="svg-wrapper">&#8594;</span></a></div>';
    box.innerHTML = h;
    box.hidden = false;
  }

  function wireInput(root) {
    if (root.dataset.dfWired) return;
    root.dataset.dfWired = '1';
    var input = root.querySelector('[data-df-fuzzy-input]');
    var box = root.querySelector('[data-df-fuzzy-results]');
    var reset = root.querySelector('[data-df-fuzzy-reset]');
    var form = root.querySelector('[data-df-fuzzy-form]');
    if (!input || !box) return;

    function options() { return Array.prototype.slice.call(box.querySelectorAll('[data-df-url]')); }
    function clearSel() { options().forEach(function (o) { o.setAttribute('aria-selected', 'false'); }); }
    function selectedIndex() { var o = options(); for (var i = 0; i < o.length; i++) if (o[i].getAttribute('aria-selected') === 'true') return i; return -1; }

    var reportSoon = debounce(function (q) { report(q, search(q)); }, REPORT_IDLE);

    var run = debounce(function () {
      var q = input.value;
      if (reset) reset.classList.toggle('hidden', !q);
      if (q.trim().length < MIN_CHARS) { renderEmptyState(box); input.setAttribute('aria-expanded', box.hidden ? 'false' : 'true'); return; }
      load().then(function () {
        renderDropdown(box, q.trim(), search(q));
        input.setAttribute('aria-expanded', box.hidden ? 'false' : 'true');
        reportSoon(q.trim());
      });
    }, DEBOUNCE);

    input.addEventListener('input', run);
    input.addEventListener('focus', function () {
      load().then(function () {
        if (input.value.trim().length >= MIN_CHARS) { if (box.innerHTML) box.hidden = false; }
        else renderEmptyState(box);
      });
    });

    input.addEventListener('keydown', function (e) {
      var o = options();
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (box.hidden || !o.length) return;
        e.preventDefault();
        var idx = selectedIndex();
        clearSel();
        idx = e.key === 'ArrowDown' ? (idx + 1) % o.length : (idx <= 0 ? o.length - 1 : idx - 1);
        o[idx].setAttribute('aria-selected', 'true');
        o[idx].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        var idx2 = selectedIndex();
        if (idx2 > -1 && o[idx2]) { e.preventDefault(); window.location.href = o[idx2].getAttribute('data-df-url'); }
      } else if (e.key === 'Escape') {
        box.hidden = true; input.setAttribute('aria-expanded', 'false');
      }
    });

    if (reset) reset.addEventListener('click', function () { input.value = ''; box.hidden = true; box.innerHTML = ''; reset.classList.add('hidden'); input.focus(); });
    if (form) form.addEventListener('submit', function (e) {
      var q = input.value.trim();
      if (!q) { e.preventDefault(); return; }
      report(q, index ? search(q) : []);
    });

    document.addEventListener('click', function (e) { if (!root.contains(e.target)) { box.hidden = true; input.setAttribute('aria-expanded', 'false'); } });
  }

  /* ---------- results page (/search) ---------- */
  function resultsPage() {
    var tpl = document.querySelector('.template-search');
    if (!tpl) return;
    var params = new URLSearchParams(location.search);
    var q = (params.get('q') || '').trim();
    if (!q) return;
    var container = document.querySelector('#ProductGridContainer');
    if (!container) return;
    load().then(function () {
      var all = search(q);
      var results = all.filter(function (it) { return it.type === 'product'; });
      report(q, all);
      var status = document.querySelector('.template-search__search ~ p[role="status"], .template-search__header p[role="status"]');
      if (!results.length) {
        /* Nothing to add. Leave Shopify's own server-rendered results
           in place rather than replacing them with a dead end. */
        return;
      }
      var h = '<ul class="df-fz-grid" role="list">';
      results.forEach(function (it) {
        h += '<li class="df-fz-card"><a href="' + esc(it.url) + '">' +
          '<span class="df-fz-imgwrap">' + (it.image ? '<img src="' + esc(it.image.replace('width=96', 'width=360')) + '" alt="" loading="lazy">' : '') + '</span>' +
          '<span class="df-fz-title">' + esc(it.title) + '</span>' +
          (it.price ? '<span class="df-fz-price">' + esc(it.price) + '</span>' : '') + '</a></li>';
      });
      h += '</ul>';
      container.innerHTML = h;
      if (status) status.textContent = results.length + (results.length === 1 ? ' result' : ' results') + ' for “' + q + '”';
      tpl.classList.remove('template-search--empty');
    });
  }

  /* ---------- init ---------- */
  function init() {
    Array.prototype.slice.call(document.querySelectorAll('[data-df-fuzzy-root]')).forEach(wireInput);
    resultsPage();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
